import { useEffect, useRef } from 'react';
import { debugLog, debugError } from '@/utils/debugLogger';

declare global {
  interface Window {
    _iub?: any[];
  }
}

/**
 * Hook to manage Iubenda privacy scripts loading
 * Loads scripts dynamically to avoid conflicts with React Router
 */
export function useIubenda() {
  const isLoadedRef = useRef(false);

  // CLS Fix: Aggiungi classe pending immediatamente
  useEffect(() => {
    document.documentElement.classList.add('iub-pending');
    return () => {
      document.documentElement.classList.remove('iub-pending');
    };
  }, []);

  useEffect(() => {
    // Avoid loading multiple times
    if (isLoadedRef.current) return;

    const loadIubenda = () => {
      try {
        // Initialize Iubenda configuration
        if (!window._iub) {
          window._iub = [];
        }
        
        // Set configuration in the correct format expected by Iubenda
        (window._iub as any).csConfiguration = {
          siteId: 4050596,
          cookiePolicyId: 85763415,
          lang: "it",
          storage: { useSiteId: true },
          // SPA configuration to prevent page reloads
          enableRemoteConsent: true,
          ccpaAcknowledgeOnDisplay: true,
          ccpaApplies: true,
          consentOnContinuedBrowsing: false,
          // Prevent automatic page reload on consent
          reloadOnConsent: false,
          // Banner UI — GDPR compliant (Rifiuta allo stesso livello di Accetta), mobile-friendly
          banner: {
            position: "float-bottom-center",
            acceptButtonDisplay: true,
            customizeButtonDisplay: true,
            rejectButtonDisplay: true,
            closeButtonDisplay: false,
            listPurposes: true,
            explicitWithdrawal: true,
            fontSizeBody: "13px",
            textColor: "#111827",
            backgroundColor: "#ffffff",
            acceptButtonColor: "#c9a227",
            acceptButtonCaptionColor: "#0b0b0b",
            rejectButtonColor: "#ffffff",
            rejectButtonCaptionColor: "#111827",
            customizeButtonColor: "#f5f5f5",
            customizeButtonCaptionColor: "#111827",
            acceptButtonCaption: "Accetta",
            rejectButtonCaption: "Rifiuta",
            customizeButtonCaption: "Personalizza",
            content: "Usiamo cookie tecnici e, con il tuo consenso, di terze parti per esperienza, misurazione e marketing. Puoi accettare, rifiutare o personalizzare.",
            backgroundOverlay: false
          },
          // Configure for React SPA
          callback: {
            onConsentGiven: function() {
              document.documentElement.classList.add('iub-consent-given');
              document.documentElement.classList.remove('iub-pending');
              debugLog('general', 'Consent given', undefined, 'Iubenda');
            },
            onConsentRejected: function() {
              document.documentElement.classList.add('iub-consent-rejected');
              document.documentElement.classList.remove('iub-pending');
              debugLog('general', 'Consent rejected', undefined, 'Iubenda');
            },
            onPreferenceExpressed: function() {
              debugLog('general', 'Preference expressed', undefined, 'Iubenda');
            }
          }
        };

        // Load autoblocking script
        const autoblockingScript = document.createElement('script');
        autoblockingScript.type = 'text/javascript';
        autoblockingScript.src = 'https://cs.iubenda.com/autoblocking/4050596.js';
        autoblockingScript.async = true;
        document.head.appendChild(autoblockingScript);

        // Load main Iubenda script
        const mainScript = document.createElement('script');
        mainScript.type = 'text/javascript';
        mainScript.src = '//cdn.iubenda.com/cs/iubenda_cs.js';
        mainScript.charset = 'UTF-8';
        mainScript.async = true;
        document.head.appendChild(mainScript);

        isLoadedRef.current = true;
        debugLog('general', 'Scripts loaded successfully', undefined, 'Iubenda');
      } catch (error) {
        debugError('general', 'Error loading scripts:', error, 'Iubenda');
      }
    };

    // Carica Iubenda dopo che la pagina è completamente caricata (window.load)
    // Questo migliora TBT e TTI rispetto al timeout fisso

    // Se la pagina è già caricata, esegui con un piccolo delay
    if (document.readyState === 'complete') {
      const timeoutId = setTimeout(loadIubenda, 100);
      return () => clearTimeout(timeoutId);
    }

    // Altrimenti aspetta l'evento load
    const handleLoad = () => {
      // Piccolo delay post-load per non bloccare il main thread
      setTimeout(loadIubenda, 100);
    };

    window.addEventListener('load', handleLoad);

    // Fallback: se load non scatta entro 5s (edge case), carica comunque
    const fallbackTimeout = setTimeout(() => {
      window.removeEventListener('load', handleLoad);
      if (!isLoadedRef.current) {
        loadIubenda();
      }
    }, 5000);

    return () => {
      window.removeEventListener('load', handleLoad);
      clearTimeout(fallbackTimeout);
    };
  }, []);

  return { isLoaded: isLoadedRef.current };
}